"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useRef, useEffect } from "react";
import { ForwardedTabWrapper } from "./ForwardedTabWrapper/index.js";
import { Tab } from "./Tab/index.js";
import { TabPanel } from "./TabPanel/index.js";
import { TabAreaContainer, SectionHeadingText, TabsContainer, StyledTabs, TabPanelContainer } from "./styles.js";
import { getTabPanelId, getTabId } from "./util.js";
import { getDataTestId } from "../../Util/index.js";
const COMPONENT_NAME = "Tabs";
const Tabs = forwardRef(
  ({
    ariaLabel,
    id,
    sectionHeaderText,
    sectionHeaderComponent = "h2",
    tabsContent
  }, ref) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const tabPanelRefs = useRef([]);
    const isMounted = useRef(false);
    useEffect(() => {
      var _a;
      if (isMounted.current) {
        if (tabPanelRefs.current[selectedTabIndex]) {
          (_a = tabPanelRefs.current[selectedTabIndex]) == null ? void 0 : _a.focus();
        }
      } else {
        isMounted.current = true;
      }
    }, [selectedTabIndex]);
    if (!tabsContent || tabsContent.length === 0) {
      return null;
    }
    const handleChange = (_, newValue) => {
      setSelectedTabIndex(newValue);
    };
    const tabs = [];
    const tabPanels = [];
    tabsContent.forEach((tabInfo, index) => {
      const panelId = getTabPanelId(id, tabInfo.id, index);
      const tabId = getTabId(id, tabInfo.id, index);
      const TabWrapper = tabInfo.TabWrapper;
      const tabWrapperProps = tabInfo.tabWrapperProps;
      tabs.push(
        /* @__PURE__ */ jsx(
          ForwardedTabWrapper,
          {
            TabWrapper,
            tabWrapperProps,
            children: /* @__PURE__ */ jsx(
              Tab,
              {
                ariaControls: panelId,
                id: tabId,
                isLastTab: index === tabsContent.length - 1,
                label: tabInfo.label
              }
            )
          },
          tabId
        )
      );
      tabPanels.push(
        /* @__PURE__ */ jsx(
          TabPanel,
          {
            ariaLabelledBy: tabId,
            selectedTabIndex,
            id: panelId,
            index,
            ref: (el) => tabPanelRefs.current[index] = el,
            children: tabInfo.panelContent
          },
          panelId
        )
      );
    });
    return /* @__PURE__ */ jsxs(TabAreaContainer, { children: [
      !!sectionHeaderText && /* @__PURE__ */ jsx(SectionHeadingText, { component: sectionHeaderComponent, children: sectionHeaderText }),
      /* @__PURE__ */ jsx(TabsContainer, { children: /* @__PURE__ */ jsx(
        StyledTabs,
        {
          TabIndicatorProps: {
            children: /* @__PURE__ */ jsx("span", { className: "MuiTabs-indicatorSpan" })
          },
          "data-testid": getDataTestId(COMPONENT_NAME),
          value: selectedTabIndex,
          onChange: handleChange,
          "aria-label": ariaLabel,
          selectionFollowsFocus: false,
          ref,
          scrollButtons: false,
          variant: "scrollable",
          children: tabs
        }
      ) }),
      /* @__PURE__ */ jsx(TabPanelContainer, { children: tabPanels })
    ] });
  }
);
Tabs.displayName = COMPONENT_NAME;
export {
  Tabs
};
